import { useState, useEffect } from 'react';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';

interface StateInfo {
  name: string;
  rate: string;
  type: string;
  notes: string;
}

export default function StateTaxComparison() {
  useEffect(() => {
    document.title = "State Tax Comparison Calculator 2025 | Compare State Income Taxes - ItsJustTax";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Compare state income tax rates across all 50 US states. Calculate and compare state tax liability, effective tax rates, and total tax burden. Find the best state for your tax situation in 2025.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'state tax comparison, state income tax rates, compare state taxes, state tax calculator, best states for taxes');
    }
  }, []);

  const [income, setIncome] = useState('');
  const [state1, setState1] = useState('california');
  const [state2, setState2] = useState('texas');
  const [filingStatus, setFilingStatus] = useState('single');
  const [result, setResult] = useState<any>(null);
  const [scrolled, setScrolled] = useState(false);

  const stateInfo: Record<string, StateInfo> = {
    california: { name: 'California', rate: '1% - 13.3%', type: 'Progressive', notes: 'Highest top rate in US' },
    newyork: { name: 'New York', rate: '4% - 10.9%', type: 'Progressive', notes: 'NYC has additional tax' },
    texas: { name: 'Texas', rate: '0%', type: 'No Income Tax', notes: 'No state income tax' },
    florida: { name: 'Florida', rate: '0%', type: 'No Income Tax', notes: 'No state income tax' },
    washington: { name: 'Washington', rate: '0%', type: 'No Income Tax', notes: 'No state income tax' },
    nevada: { name: 'Nevada', rate: '0%', type: 'No Income Tax', notes: 'No state income tax' },
    illinois: { name: 'Illinois', rate: '4.95%', type: 'Flat', notes: 'Flat rate for all income' },
    pennsylvania: { name: 'Pennsylvania', rate: '3.07%', type: 'Flat', notes: 'Flat rate for all income' },
    massachusetts: { name: 'Massachusetts', rate: '5%', type: 'Flat', notes: 'Flat rate for all income' },
    colorado: { name: 'Colorado', rate: '4.4%', type: 'Flat', notes: 'Flat rate for all income' },
    oregon: { name: 'Oregon', rate: '4.75% - 9.9%', type: 'Progressive', notes: 'No sales tax' },
    newjersey: { name: 'New Jersey', rate: '1.4% - 10.75%', type: 'Progressive', notes: 'High property taxes' }
  };

  const calculateStateTax = (state: string, income: number) => {
    if (state === 'california') {
      if (filingStatus === 'single') {
        if (income <= 10412) return income * 0.01;
        if (income <= 24684) return 104.12 + (income - 10412) * 0.02;
        if (income <= 38959) return 389.56 + (income - 24684) * 0.04;
        if (income <= 54081) return 960.56 + (income - 38959) * 0.06;
        if (income <= 68350) return 1867.88 + (income - 54081) * 0.08;
        if (income <= 349137) return 3009.40 + (income - 68350) * 0.093;
        if (income <= 418961) return 29122.50 + (income - 349137) * 0.103;
        if (income <= 698271) return 36314.16 + (income - 418961) * 0.113;
        return 67880.12 + (income - 698271) * 0.133;
      } else {
        if (income <= 20824) return income * 0.01;
        if (income <= 49368) return 208.24 + (income - 20824) * 0.02;
        if (income <= 77918) return 779.12 + (income - 49368) * 0.04;
        if (income <= 108162) return 1921.12 + (income - 77918) * 0.06;
        if (income <= 136700) return 3735.76 + (income - 108162) * 0.08;
        return 6018.80 + (income - 136700) * 0.093;
      }
    } else if (state === 'newyork') {
      if (filingStatus === 'single') {
        if (income <= 8500) return income * 0.04;
        if (income <= 11700) return 340 + (income - 8500) * 0.045;
        if (income <= 13900) return 484 + (income - 11700) * 0.0525;
        if (income <= 80650) return 600 + (income - 13900) * 0.055;
        if (income <= 215400) return 4271.25 + (income - 80650) * 0.06;
        if (income <= 1077550) return 12356.25 + (income - 215400) * 0.0685;
        return 71412.28 + (income - 1077550) * 0.109;
      } else {
        if (income <= 17150) return income * 0.04;
        if (income <= 23600) return 686 + (income - 17150) * 0.045;
        if (income <= 27900) return 976.25 + (income - 23600) * 0.0525;
        if (income <= 161550) return 1202 + (income - 27900) * 0.055;
        return 8552.75 + (income - 161550) * 0.06;
      }
    } else if (['texas', 'florida', 'washington', 'nevada'].includes(state)) {
      return 0;
    } else if (state === 'illinois') {
      return income * 0.0495;
    } else if (state === 'pennsylvania') {
      return income * 0.0307;
    } else if (state === 'massachusetts') {
      return income * 0.05;
    } else if (state === 'colorado') {
      return income * 0.044;
    } else if (state === 'oregon') {
      if (filingStatus === 'single') {
        if (income <= 3750) return income * 0.0475;
        if (income <= 9450) return 178.13 + (income - 3750) * 0.0675;
        if (income <= 125000) return 562.88 + (income - 9450) * 0.0875;
        return 10673.13 + (income - 125000) * 0.099;
      } else {
        if (income <= 7500) return income * 0.0475;
        if (income <= 18900) return 356.25 + (income - 7500) * 0.0675;
        if (income <= 250000) return 1125.75 + (income - 18900) * 0.0875;
        return 21346.25 + (income - 250000) * 0.099;
      }
    } else if (state === 'newjersey') {
      if (filingStatus === 'single') {
        if (income <= 20000) return income * 0.014;
        if (income <= 35000) return 280 + (income - 20000) * 0.0175;
        if (income <= 40000) return 542.50 + (income - 35000) * 0.035;
        if (income <= 75000) return 717.50 + (income - 40000) * 0.05525;
        if (income <= 500000) return 2651.25 + (income - 75000) * 0.0637;
        if (income <= 1000000) return 29723.75 + (income - 500000) * 0.0897;
        return 74573.75 + (income - 1000000) * 0.1075;
      } else {
        if (income <= 20000) return income * 0.014;
        if (income <= 50000) return 280 + (income - 20000) * 0.0175;
        if (income <= 70000) return 805 + (income - 50000) * 0.0245;
        if (income <= 80000) return 1295 + (income - 70000) * 0.035;
        return 1645 + (income - 80000) * 0.05525;
      }
    }
    return 0;
  };

  const calculate = () => {
    const annualIncome = parseFloat(income);
    if (isNaN(annualIncome) || annualIncome <= 0) {
      alert('Please enter a valid income amount');
      return;
    }

    const state1Tax = calculateStateTax(state1, annualIncome);
    const state2Tax = calculateStateTax(state2, annualIncome);
    const difference = Math.abs(state1Tax - state2Tax);
    const savings = state1Tax > state2Tax ? state2 : state1;

    setResult({
      income: annualIncome,
      state1: {
        name: stateInfo[state1].name,
        tax: state1Tax,
        rate: stateInfo[state1].rate,
        type: stateInfo[state1].type,
        afterTax: annualIncome - state1Tax
      },
      state2: {
        name: stateInfo[state2].name,
        tax: state2Tax,
        rate: stateInfo[state2].rate,
        type: stateInfo[state2].type,
        afterTax: annualIncome - state2Tax
      },
      difference,
      savings: stateInfo[savings].name
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation scrolled={scrolled} />
      
      <div className="pt-32 pb-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-8">
            <a href="/" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 mb-4 cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </a>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">State Tax Comparison</h1>
            <p className="text-lg text-gray-600">Compare state income tax rates and see how much you could save</p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Annual Income</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                  <input
                    type="number"
                    value={income}
                    onChange={(e) => setIncome(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                    placeholder="100000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Filing Status</label>
                <select
                  value={filingStatus}
                  onChange={(e) => setFilingStatus(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg cursor-pointer"
                >
                  <option value="single">Single</option>
                  <option value="married">Married Filing Jointly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">State 1</label>
                <select
                  value={state1}
                  onChange={(e) => setState1(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg cursor-pointer"
                >
                  {Object.entries(stateInfo).map(([key, info]) => (
                    <option key={key} value={key}>{info.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">State 2</label>
                <select
                  value={state2}
                  onChange={(e) => setState2(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg cursor-pointer"
                >
                  {Object.entries(stateInfo).map(([key, info]) => (
                    <option key={key} value={key}>{info.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              onClick={calculate}
              className="w-full mt-6 py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl font-semibold hover:from-teal-700 hover:to-teal-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Compare States
            </button>
          </div>

          {result && (
            <>
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <i className="ri-map-pin-line text-2xl text-blue-600"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">{result.state1.name}</h3>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="text-sm text-gray-600 mb-1">Tax Rate</div>
                      <div className="text-xl font-bold text-gray-900">{result.state1.rate}</div>
                      <div className="text-sm text-gray-500 mt-1">{result.state1.type}</div>
                    </div>

                    <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                      <span className="text-gray-600">Gross Income</span>
                      <span className="font-semibold">${result.income.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                      <span className="text-gray-600">State Tax</span>
                      <span className="font-semibold text-red-600">-${result.state1.tax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 bg-blue-50 rounded-xl p-4">
                      <span className="font-semibold text-gray-900">After-Tax Income</span>
                      <span className="text-2xl font-bold text-blue-600">${result.state1.afterTax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                      <i className="ri-map-pin-line text-2xl text-teal-600"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">{result.state2.name}</h3>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="text-sm text-gray-600 mb-1">Tax Rate</div>
                      <div className="text-xl font-bold text-gray-900">{result.state2.rate}</div>
                      <div className="text-sm text-gray-500 mt-1">{result.state2.type}</div>
                    </div>

                    <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                      <span className="text-gray-600">Gross Income</span>
                      <span className="font-semibold">${result.income.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                      <span className="text-gray-600">State Tax</span>
                      <span className="font-semibold text-red-600">-${result.state2.tax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 bg-teal-50 rounded-xl p-4">
                      <span className="font-semibold text-gray-900">After-Tax Income</span>
                      <span className="text-2xl font-bold text-teal-600">${result.state2.afterTax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 shadow-lg text-white">
                <h3 className="text-2xl font-bold mb-4">Comparison Summary</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-teal-100 mb-2">Annual Tax Difference</p>
                    <p className="text-4xl font-bold mb-4">${result.difference.toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
                    <p className="text-teal-100">
                      Living in <strong>{result.savings}</strong> saves you more on state income taxes
                    </p>
                  </div>
                  <div className="text-6xl">💰</div>
                </div>
                <div className="mt-6 bg-white/10 rounded-xl p-4">
                  <p className="text-teal-100 text-sm leading-relaxed">
                    <strong>Important:</strong> This comparison only includes state income tax. Consider other factors like 
                    property taxes, sales taxes, cost of living, and quality of life when choosing where to live.
                  </p>
                </div>
              </div>
            </>
          )}

          <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">States with No Income Tax</h3>
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
              {['Alaska', 'Florida', 'Nevada', 'South Dakota', 'Tennessee', 'Texas', 'Washington', 'Wyoming'].map(state => (
                <div key={state} className="flex items-center gap-3 p-4 bg-green-50 rounded-xl">
                  <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                  <span className="font-semibold text-gray-900">{state}</span>
                </div>
              ))}
            </div>
            <p className="text-gray-600 text-sm mt-6 leading-relaxed">
              These states don't impose a state income tax on wages. However, they may have higher property taxes, 
              sales taxes, or other fees to fund state services.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}